import { keepPreviousData, type QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import isObject from 'lodash/isObject';
import { useMemo } from 'react';

import { type BoardsListOptions, SortDirection } from '@air/api/types';

import { usePrivateAddOnFeatures } from '~/hooks/usePrivateAddOnFeatures';
import { usePrivateBoardsFetcher } from '~/swr-hooks/gallery/galleryView/usePrivateBoardsFetcher';
import { useSubnavSort } from '~/swr-hooks/subnav/useSubnavSort';

export const BOARDS_LIST = 'BOARDS_LIST';

export type WorkspaceBoardsKeyParams = Pick<
  Partial<BoardsListOptions>,
  | 'sortField'
  | 'libraryBoards'
  | 'includeDirectDescendantBoardCount'
  | 'includeDirectDescendantCount'
  | 'view'
  | 'search'
> & { workspaceId?: string };

export type WorkspaceBoardsKey = [typeof BOARDS_LIST, WorkspaceBoardsKeyParams];

export type UseWorkspaceBoardsOptions = {
  workspaceId?: string;
} & Pick<
  BoardsListOptions,
  'libraryBoards' | 'includeDirectDescendantBoardCount' | 'includeDirectDescendantCount' | 'view' | 'search'
>;

export const getWorkspaceBoardsKey = (params: WorkspaceBoardsKeyParams): WorkspaceBoardsKey => {
  return [BOARDS_LIST, params];
};

export const isWorkspaceBoardsKey = (key: QueryKey): key is WorkspaceBoardsKey => {
  return Array.isArray(key) && key[0] === BOARDS_LIST && isObject(key[1]);
};

export const useWorkspaceBoards = ({ workspaceId, view, ...params }: UseWorkspaceBoardsOptions) => {
  const { privateBoardsFetcher } = usePrivateBoardsFetcher();
  const { data: subnavSort } = useSubnavSort();
  const sortField = subnavSort?.boardSort;
  const { libraries: librariesFlag } = usePrivateAddOnFeatures();
  // Unless explicitly overridden, if libraries feature is turned off we should show libraries root boards in workspace boards
  const libraryBoards = params.libraryBoards ?? (librariesFlag ? 'NO' : 'ALL');

  const infiniteQuery = useInfiniteQuery({
    queryKey: getWorkspaceBoardsKey({
      workspaceId,
      view,
      libraryBoards,
      sortField,
      includeDirectDescendantBoardCount: params.includeDirectDescendantBoardCount,
      includeDirectDescendantCount: params.includeDirectDescendantCount,
      search: params.search,
    }),
    queryFn: ({ pageParam }) => {
      const finalParams = {
        isMemberOfBoard: 'ALL',
        limit: 100,
        view,
        includeAncestors: true,
        numThumbnails: 0,
        sortField,
        libraryBoards,
        includeDirectDescendantBoardCount: params.includeDirectDescendantBoardCount,
        includeDirectDescendantCount: params.includeDirectDescendantCount,
        search: params.search,
      } satisfies Partial<BoardsListOptions>;

      return privateBoardsFetcher({
        params: finalParams,
        sortFieldName: sortField?.name || 'customSort',
        sortDirection: sortField?.direction || SortDirection.desc,
        cursor: pageParam || null,
      });
    },
    enabled: !!workspaceId && !!sortField,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
    placeholderData: keepPreviousData,
  });

  const data = useMemo(() => infiniteQuery.data?.pages.flatMap((d) => d.data) ?? [], [infiniteQuery.data?.pages]);
  const total = infiniteQuery.data?.pages[infiniteQuery.data.pages.length - 1]?.total ?? 0;

  return {
    data,
    total,
    hasMore: !!infiniteQuery.hasNextPage,
    loadNextPage: infiniteQuery.fetchNextPage,
    isLoadingMore: infiniteQuery.isFetchingNextPage,
    isInitialLoading: infiniteQuery.isLoading,
  };
};
